<template>
  <b-overlay
    :show="$store.state.app.isContentLoading"
    spinner-variant="primary"
    spinner-type="grow"
    rounded="sm"
  >

    <div>

      <!-- Filters -->
      <inventory-list-filters
        :branch-filter.sync="branchFilter"
        :branch-options="branchOptions"
        :category-filter.sync="categoryFilter"
        :category-options="categoryOptions"
      />

      <!-- Table Container Card -->
      <b-card
        :title="`${$t('Inventory')} ${$t('Items')}`"
        class="mb-0"
      >

        <div class="mb-2 mt-2">

          <!-- Table Top -->
          <b-row>

            <!-- Per Page -->
            <b-col
              cols="12"
              md="6"
              class="d-flex align-items-center justify-content-start pl-0 mb-1 mb-md-0"
            >
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block mx-50"
              />
            </b-col>

            <!-- Search -->
            <b-col
              cols="12"
              md="6"
            >
              <div class="d-flex align-items-center justify-content-end">
                <b-form-input
                  v-model="searchQuery"
                  class="d-inline-block mr-1"
                  :placeholder="$t('Search')"
                  @keyup.enter="search"
                />
                <b-button
                  v-if="$can('create', 'inventory')"
                  class="mr-1"
                  variant="primary"
                  @click="add"
                >
                  <span class="text-nowrap">{{ this.$t('New') }}</span>
                </b-button>
                <!-- form -->
                <b-dropdown
                  id="dropdown-form"
                  ref="dropdown"
                  right
                  variant="outline-secondary"
                >
                  <template #button-content>
                    <feather-icon
                      icon="GridIcon"
                      size="16"
                      class="align-middle text-body"
                    />
                  </template>
                  <b-dropdown-form class="py-0">

                    <b-form-checkbox
                      v-for="field in tableColumns"
                      :key="field.key"
                      v-model="field.visible"
                      class="mb-1"
                      :disabled="visibleFields.length == 1 && field.visible"
                      inline
                    >
                      {{ field.label }}
                    </b-form-checkbox>

                  </b-dropdown-form>
                  <b-dropdown-divider />
                  <b-dropdown-item>Download</b-dropdown-item>
                </b-dropdown>
              </div>
            </b-col>
          </b-row>

        </div>

        <b-table
          ref="refInventoryItemListTable"
          class="position-relative"
          :items="fetchInventories"
          responsive
          :fields="visibleFields"
          primary-key="id"
          :sort-by.sync="sortBy"
          show-empty
          empty-text="No matching records found"
          :sort-desc.sync="isSortDirDesc"
          stacked="sm"
        >

          <!-- Column: Actions -->
          <template #cell(actions)="data">
            <b-dropdown
              variant="link"
              no-caret
              :right="$store.state.appConfig.isRTL"
            >

              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>

              <b-dropdown-item
                v-if="$can('update', 'inventory')"
                :to="{ name: 'inventory-edit', params: { id: data.item.id } }"
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50">Edit</span>
              </b-dropdown-item>

              <b-dropdown-item
                v-if="$can('delete', 'inventory')"
              >
                <feather-icon icon="TrashIcon" />
                <span
                  class="align-middle ml-50"
                  @click="del(data.item.id)"
                >
                  Delete</span>
              </b-dropdown-item>
            </b-dropdown>
          </template>

        </b-table>
        <div class="mx-2 mb-2">
          <b-row>

            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted">Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries</span>
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >

              <b-pagination
                v-model="currentPage"
                :total-rows="totalInventoryItems"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>

            </b-col>

          </b-row>
        </div>
      </b-card>

    </div>

  </b-overlay>
</template>

<script>
import {
  BOverlay, BCard, BRow, BCol, BFormInput, BButton, BTable,
  BDropdown, BDropdownItem, BPagination, BDropdownForm, BFormCheckbox, BDropdownDivider,
} from 'bootstrap-vue'
import vSelect from '@/libs/vue-select'
import store from '@/store'
import { onUnmounted } from '@vue/composition-api'
import InventoryListFilters from './InventoryListFilters.vue'
import storeModule from '../../../common/storeModule'
import useInventoryList from './useInventoryList'
import inventoryStoreModule from '../inventoryStoreModule'

export default {
  components: {
    InventoryListFilters,
    BOverlay,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BDropdownForm,
    BFormCheckbox,
    BDropdownDivider,
    vSelect,
  },
  data() {
    return {
      branchOptions: [],
      categoryOptions: [],
      // Table Handlers
      tableColumns: [
        {
          key: 'item_name', label: this.$t('Item'), sortable: true, visible: true,
        },
        {
          key: 'sale_price', label: this.$t('Sale Price'), sortable: true, visible: true,
        },
        {
          key: 'opening_quantity', label: this.$t('Opening'), sortable: true, visible: true,
        },
        {
          key: 'closing_quantity', label: this.$t('Closing'), sortable: true, visible: true,
        },
        {
          key: 'minimum_quantity', label: this.$t('Minimum'), sortable: true, visible: true,
        },
        {
          key: 'unit_name', label: this.$t('Unit'), sortable: true, visible: true,
        },
        {
          key: 'branch_name',
          label: this.$t('Branch'),
          sortable: true,
          visible: this.$can('audit', 'all'),
        },
        { key: 'actions', label: this.$t('Actions'), visible: true },
      ],
    }
  },
  computed: {
    visibleFields() {
      return this.tableColumns.filter(field => field.visible)
    },
  },
  setup() {
    const STORE_MODULE_NAME = 'inventory'

    // Register module
    if (!store.hasModule('common')) store.registerModule('common', storeModule)
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, inventoryStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const {
      fetchInventories,
      perPage,
      currentPage,
      totalInventoryItems,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refInventoryItemListTable,
      refetchData,

      // Filters
      branchFilter,
      categoryFilter,

    } = useInventoryList()

    return {

      fetchInventories,
      perPage,
      currentPage,
      totalInventoryItems,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refInventoryItemListTable,
      refetchData,

      // Extra Filters
      branchFilter,
      categoryFilter,

    }
  },
  mounted() {
    this.loadBranches()
    this.loadCategories()
  },
  methods: {
    loadBranches() {
      store
        .dispatch('common/fetchBranches')
        .then(response => {
          const { branches } = response.data.data
          for (let i = 0; i < branches.length; i += 1) {
            this.branchOptions.push({ label: branches[i].name, value: branches[i].id.toString() })
          }
        })
        .catch(error => {
          console.log(error)
        })
    },
    loadCategories() {
      store
        .dispatch('common/fetchCategories')
        .then(response => {
          const { categories } = response.data.data
          for (let i = 0; i < categories.length; i += 1) {
            this.categoryOptions.push({ label: categories[i].name, value: categories[i].id.toString() })
          }
        })
        .catch(error => {
          console.log(error)
        })
    },
    search() {
      this.refetchData()
    },
    add() {
      this.$router.push({ name: 'inventory-new' })
    },
    del(id) {
      const that = this
      this.$swal({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          store.dispatch('inventory/del', id)
            .then(response => {
              if (response.status === 200) {
                that.refetchData()
              } else {
                console.log(response)
              }
            })
            .catch(error => {
              window.swal('Error', JSON.stringify(error.response.data).replace(/"([^"]+)":/g, '$1:'))
            })
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
